import React, { useState, useEffect, useMemo, useRef } from "react";
import { FiPlus, FiX } from 'react-icons/fi';
import Clienteform from "../../components/Fomulario/formcliente.jsx";
import ListaClientes from "../../components/Tabela/tabcliente.jsx";
import styles from "./cliente.module.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from '../../components/Spinner/carregando';
import ui from "../../styles/ui.module.css";
import api from "../../services/api";
import useConfirmDialog from "../../components/ConfirmModal/useConfirmDialog.jsx";


// API centralizada via services/api

const Clientes = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [formData, setFormData] = useState({ id: "", nome: "", cpf: "", cep: "", endereco: "", numero: "", complemento: "", bairro: "", uf: "", cidade: "", codigoMunicipio: "", telefone: "", email: "" });
  const [filtros, setFiltros] = useState({ nome: '', cpf: '' });
  const [editing, setEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [sortBy, setSortBy] = useState('id');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const nomeBuscaRef = useRef(null);
  const { confirm, ConfirmModal } = useConfirmDialog({
    confirmClassName: `${ui.butdesktop} ${ui.btnPrimary}`,
    cancelClassName: ui.butdesktop,
  });
  const exportToCsv = () => {
    const headers = ['ID','NOME','CPF','CEP','ENDERECO','NUMERO','BAIRRO','CIDADE','UF','TELEFONE','EMAIL'];
    const lines = [headers.join(';')].concat(
      clientesFiltrados.map(c => [
        c.ID,
        (c.NOME||'').replaceAll(';',','),
        c.CPF || '',
        c.CEP || '',
        (c.ENDERECO||'').replaceAll(';',','),
        c.NUMERO || '',
        (c.BAIRRO||'').replaceAll(';',','),
        (c.CIDADE||'').replaceAll(';',','),
        c.UF || '',
        (c.WHATSAPP_NUMERO || c.TELEFONE || ''),
        (c.EMAIL || '').replaceAll(';',',')
      ].join(';'))
    );
    const blob = new Blob(["\uFEFF" + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clientes.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const carregarClientes = async () => {
    try {
      setIsLoading(true);
      const resp = await api.get("/clientes");
      setClientes(Array.isArray(resp.data) ? resp.data : []);
    } catch (erro) {
      const backendMsg = erro?.response?.data?.message || erro?.response?.data?.error || (typeof erro?.response?.data === 'string' ? erro.response.data : '') || erro?.message || 'Erro ao carregar clientes.';
      toast.error(`Erro ao carregar clientes: ${backendMsg}`);
      console.error("Erro ao carregar clientes:", erro?.response?.data || erro);
    } finally {
      setIsLoading(false);
    }
  };

  const removerCpfCnpjInvalidos = async () => {
    const confirmar = await confirm("Remover CPF/CNPJ inválidos (padrão de zeros) de todos os clientes?");
    if (!confirmar) return;
    try {
      setIsLoading(true);
      const resp = await api.post("/clientes/cleanup-invalid-cpf");
      const updated = resp?.data?.updated ?? 0;
      if (updated > 0) {
        toast.success(`CPF/CNPJ inválidos removidos: ${updated}`);
      } else {
        toast.info("Nenhum CPF/CNPJ inválido encontrado.");
      }
      await carregarClientes();
    } catch (erro) {
      const backendMsg = erro?.response?.data?.message || erro?.response?.data?.error || (typeof erro?.response?.data === 'string' ? erro.response.data : '') || erro?.message || 'Erro ao limpar CPF/CNPJ inválidos.';
      toast.error(`Erro ao limpar CPF/CNPJ inválidos: ${backendMsg}`);
      console.error("Erro ao limpar CPF/CNPJ inválidos:", erro?.response?.data || erro);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    carregarClientes();
  }, []);

  // Fecha modal com tecla ESC
  useEffect(() => {
    if (!showForm) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowForm(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showForm]);

  // Validação simples de CPF/CNPJ
  const validarCpfCnpj = (cpf) => {
    const onlyDigits = (cpf || '').replace(/\D/g, '');
    return onlyDigits.length === 0 || /^(\d{11}|\d{14})$/.test(onlyDigits);
  };
  const maskCpfCnpj = (value) => {
    const digits = (value || '').replace(/\D/g, '');
    if (digits.length <= 11) {
      return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return digits
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };

  const adicionarCliente = async (payload) => {
    const sanitized = {
      ...payload,
      cpf: (payload.cpf || '').replace(/\D/g, ''),
      cep: (payload.cep || '').replace(/\D/g, ''),
      uf: (payload.uf || '').toUpperCase(),
      codigoMunicipio: (payload.codigoMunicipio || '').replace(/\D/g, ''),
      whatsappNumero: (payload.telefone || '').replace(/\D/g, ''),
    };
    if (!sanitized.nome || !validarCpfCnpj(sanitized.cpf)) {
      toast.warn("Preencha um nome válido e CPF/CNPJ com 11 ou 14 dígitos (ou deixe em branco).");
      return;
    }
    try {
      setIsLoading(true);
      if (sanitized.id) {
        await api.put(`/clientes/${sanitized.id}`, sanitized);
        toast.success("Cliente atualizado!");
      } else {
        await api.post("/clientes", sanitized);
        toast.success("Cliente adicionado!");
      }
      await carregarClientes();
      setFormData({ id: "", nome: "", cpf: "", cep: "", endereco: "", numero: "", complemento: "", bairro: "", uf: "", cidade: "", codigoMunicipio: "", telefone: "", email: "" });
      setEditing(false);
      setShowForm(false);
    } catch (erro) {
      const backendMsg = erro?.response?.data?.message || erro?.response?.data?.error || (typeof erro?.response?.data === 'string' ? erro.response.data : '') || erro?.message || 'Erro ao salvar cliente.';
      toast.error(`Erro ao salvar cliente: ${backendMsg}`);
      console.error("Erro ao salvar cliente:", erro?.response?.data || erro);
    } finally {
      setIsLoading(false);
    }
  };

  const editarCliente = (id) => {
    const cliente = clientes.find((c) => c.ID === id);
    if (!cliente) return;
    setFormData({
      id: cliente.ID,
      nome: cliente.NOME,
      cpf: cliente.CPF,
      cep: cliente.CEP || '',
      endereco: cliente.ENDERECO || '',
      numero: cliente.NUMERO || '',
      complemento: cliente.COMPLEMENTO || '',
      bairro: cliente.BAIRRO || '',
      uf: cliente.UF || '',
      cidade: cliente.CIDADE || '',
      codigoMunicipio: cliente.CODIGOMUNICIPIO || '',
      telefone: cliente.WHATSAPP_NUMERO || cliente.TELEFONE || '',
      email: cliente.EMAIL || '',
    });
    setEditing(true);
    setShowForm(true);
  };
  //console.log("ln 84, formData:", formData);

  const deletarCliente = async (id) => {
    const cliente = clientes.find((c) => c.ID === id);
    if (!cliente) return;
    const confirmar = await confirm(`Remover cliente "${cliente.NOME}"?`);
    if (!confirmar) return;
    try {
      setIsLoading(true);
      await api.delete(`/clientes/${cliente.ID}`);
      toast.success("Cliente deletado!");
      await carregarClientes();
      if (editing && formData.id === id) {
        setFormData({ id: "", nome: "", cpf: "" });
        setEditing(false);
      }
    } catch (erro) {
      const backendMsg = erro?.response?.data?.message || erro?.response?.data?.error || (typeof erro?.response?.data === 'string' ? erro.response.data : '') || erro?.message || 'Erro ao deletar cliente.';
      toast.error(`Erro ao deletar cliente: ${backendMsg}`);
      console.error("Erro ao deletar cliente:", erro?.response?.data || erro);
    } finally {
      setIsLoading(false);
    }
  };

  const ordenarClientes = (criterio) => {
    if (!criterio) return;
    setSortBy(criterio);
    setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  };

  const clientesFiltrados = useMemo(() => {
    const nome = (filtros.nome || '').toUpperCase();
    const cpf = (filtros.cpf || '').replace(/\D/g, '');
    const base = (Array.isArray(clientes) ? clientes : [])
      .filter((c) => {
        const byNome = nome ? (c.NOME || '').toUpperCase().includes(nome) : true;
        const byCpf = cpf ? String(c.CPF || '').includes(cpf) : true;
        return byNome && byCpf;
      });
    const sorted = [...base].sort((a, b) => {
      if (sortBy === 'id') return sortDir === 'asc' ? a.ID - b.ID : b.ID - a.ID;
      if (sortBy === 'nome') return sortDir === 'asc' ? a.NOME.localeCompare(b.NOME) : b.NOME.localeCompare(a.NOME);
      if (sortBy === 'cpf') return sortDir === 'asc' ? a.CPF.localeCompare(b.CPF) : b.CPF.localeCompare(a.CPF);
      return 0;
    });
    return sorted;
  }, [clientes, filtros, sortBy, sortDir]);

  const total = clientesFiltrados.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(page, totalPages);
  const startIndex = (pageSafe - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);
  const pageItems = clientesFiltrados.slice(startIndex, endIndex);

  return (
    <main className={styles.root}>
      <ConfirmModal />
      <h3 className="cliente-titulo">Clientes</h3>
      <div className={styles["cliente-busca"]}>
        <div>
          <label htmlFor="buscarNome">Buscar por nome</label>
          <input
            id="buscarNome"
            type="text"
            autoComplete="name"
            placeholder="Digite um nome"
            value={filtros.nome}
            onChange={(e) => setFiltros((prev) => ({ ...prev, nome: e.target.value }))}
            ref={nomeBuscaRef}
          />
        </div>
        <div>
          <label htmlFor="buscarCpf">Buscar por CPF/CNPJ</label>
          <input
            id="buscarCpf"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="CPF: Somente números"
            value={maskCpfCnpj(filtros.cpf)}
            onChange={(e) => setFiltros((prev) => ({ ...prev, cpf: e.target.value }))}
          />
        </div>
        <div className={styles["cliente-busca-acoes"]}>
          <label htmlFor="btnlimpar">...</label>
          <button id="btnlimpar" type="button" className={`${ui.butdesktop} ${ui.btnSecondary}`} onClick={() => { setFiltros({ nome: '', cpf: '' }); nomeBuscaRef.current?.focus(); }} aria-label="Limpar filtros">Limpar</button>
          <button 
            type="button" 
            className={`${ui.butdesktop} ${ui.btnSecondary}`} 
            style={{ display: "none",}} 
            onClick={removerCpfCnpjInvalidos}>
            Remover CPF/CNPJ inválidos
          </button>
        </div>
      </div>
      <ListaClientes
        ordenarClientes={ordenarClientes}
        sortBy={sortBy}
        sortDir={sortDir}
        clientes={pageItems}
        deletarCliente={deletarCliente}
        editarCliente={editarCliente}
      />

      <div className={styles["cliente-paginacao"]}>
        <div className={styles["cliente-paginacao-info"]}>
          Mostrando {total === 0 ? 0 : startIndex + 1}–{endIndex} de {total}
        </div>
        <div className={styles["cliente-paginacao-controles"]}>
          <button className={`${ui.butdesktop}`} disabled={pageSafe <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>{"<"}</button>
          <span >Página {pageSafe} / {totalPages}</span>
          <button className={`${ui.butdesktop}`} disabled={pageSafe >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>{">"}</button>
          <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <button className={`${styles.exportar}`} onClick={exportToCsv}>Exportar CSV</button>
        </div>
      </div>

      <div className={ui.floatingFab}>
        <button title="Cadastar novo cliente!" className={`${ui.novaBtn} ${ui.addBtn}`} aria-label="Novo cliente" onClick={() => { setShowForm(true); setEditing(false); setFormData({ id: "", nome: "", cpf: "", cep: "", endereco: "", numero: "", complemento: "", bairro: "", uf: "", cidade: "", codigoMunicipio: "", telefone: "", email: "" }); }}><FiPlus/></button>
      </div>

      {showForm && (
        <div className={styles["cliente-modal"]} role="dialog" aria-modal="true" aria-label="Cadastro de Cliente" onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div className={styles["cliente-modal-card"]}>
            <div className={styles["cliente-modal-header"]}>
              <h1>{editing ? "Editar Cliente" : "Cadastro de Clientes"}</h1>
              <button className={`${ui.butfechar} ${styles["cliente-modal-close"]}`} onClick={() => setShowForm(false)} aria-label="Fechar">
                <FiX size={20} />
              </button>
            </div>
            <div className={styles["cliente-modal-body"]}>
              <Clienteform
                adicionarCliente={adicionarCliente}
                formData={formData}
                setFormData={setFormData}
                editing={editing}
                onCancel={() => { setFormData({ id: "", nome: "", cpf: "", cep: "", endereco: "", numero: "", complemento: "", bairro: "", uf: "", cidade: "", codigoMunicipio: "", telefone: "", email: "" }); setEditing(false); setShowForm(false); }}
              />
            </div>
          </div>
        </div>
      )}
      <ToastContainer limit={1} position="top-right" autoClose={3000} />
      <Spinner isLoading={isLoading} />
    </main>
  );
};

export default Clientes;
